import logoComponayPlaceholder from '@/public/images/skyline.png';
import styles from '@/styles/highlight-jobs.module.css';
import { SuperHighlightJob } from '@/types/job';
import Image from 'next/image';
import ShareJobButton from '../modals/share-job-button';
import { useCurrentWidth } from '../search/useDebounce';
import { IconPcd } from '../jobs/icon-pdc';
import { AmplitudeService } from '@/services/Amplitude.service';
import Link from '@/components/link-with-utms';
import { nameMask } from '@/utils/name-mask';
import { formatJobTitle } from '@/utils/format-job-title';

export default function SuperHighlightJobCard({ data }: SuperHighlightJobCardProps) {
  if (!data.title) {
    console.log('SuperHighlightJobCard render', { data });
  }

  let jobTitleDisplay = formatJobTitle(data.title);

  jobTitleDisplay = jobTitleDisplay.replace(/\( /g, `(`).replace(/\)/g, `)`);

  if (/\w+\/\w+/g.test(jobTitleDisplay)) {
    jobTitleDisplay = jobTitleDisplay.split(`/`).join(` / `);
  }

  // O super destaque vem de `job?ids=`, onde `slug` ja e o slug completo
  // (`403799-vaga-de-ajudante-de-obras`). Prefixar id e `-vaga-de-` aqui
  // dobrava o caminho e fazia os 6 cards responderem 308 — inclusive na URL
  // que o ShareJobButton distribui. O card de destaque normal usa outro
  // endpoint (`search-highlight-jobs`), cujo `slug` e so o cargo, e por isso
  // continua montando o caminho.
  //
  // O fallback para o id nao e decorativo: a expressao antiga carregava o id no
  // caminho, entao um `slug` ausente ainda achava a vaga (308 para o canonico).
  // Sem ele, `/vagas/emprego/undefined` da 404. `/vagas/emprego/403799` da 308
  // para o canonico — mesma rede de seguranca, sem custo no caminho normal.
  const linkToJobDetails = `/vagas/emprego/${data.slug || data.id}?utm_content=super_destaque`;

  const windowWidth = useCurrentWidth();

  if (windowWidth < 768 && jobTitleDisplay.length > 34) {
    const words = jobTitleDisplay.split(` `);
    let lastWordIndex = Math.min(words.length - 1, 4);
    while (words[lastWordIndex - 1].length < 2 && lastWordIndex > 1) {
      lastWordIndex--;
    }
    if (words[lastWordIndex - 1].length <= 2) {
      lastWordIndex--;
    }
    jobTitleDisplay = words.slice(0, lastWordIndex).join(` `);
    jobTitleDisplay = jobTitleDisplay.slice(0, jobTitleDisplay.length) + `...`;
  }

  const renderLocation = Boolean(data.city && data.state);

  const companyNameFormatted = data.companyName.toLowerCase().includes('empresa confidencial')
    ? 'Empresa Confidencial'
    : nameMask(data.companyName);

  return (
    <div className={styles.highlight_box}>
      <Link href={linkToJobDetails} onClick={() => AmplitudeService.track(`super_destaque_click`, { vagaId: data.id })}>
        <div className={styles.highlight_company}>
          <div className={styles.top}>
            <div className={styles.img_container}>
              <Image
                width={58}
                height={58}
                // O logo vem da API na proporcao que a empresa mandou — o
                // Lighthouse pegou um 250x327 sendo espremido neste quadrado de
                // 58x58. `contain` mostra o logo inteiro, com folga nas laterais,
                // em vez de distorcer a marca de quem anuncia.
                style={{ objectFit: `contain` }}
                src={data.companyLogo || logoComponayPlaceholder}
                alt={`Logo da empresa ${companyNameFormatted}`}
              />
            </div>
            <p className={styles.highlight_responsibility}>
              {jobTitleDisplay} {data.pcd && <IconPcd />}
            </p>
          </div>
          <p className={styles.highlight_company_name}>{companyNameFormatted}</p>
        </div>

        {renderLocation && (
          <div className={styles.highlight_data}>
            <p className={styles.highlight_localization}>
              {data.city} - {data.state}
            </p>
          </div>
        )}

        <span className="btn btn-secondary btn-small" style={{ margin: `0 auto` }}>
          Quero me candidatar
        </span>
      </Link>
      <ul className={styles.highlight_share}>
        <li>
          <ShareJobButton link={process.env.NEXT_PUBLIC_BASE_REF + linkToJobDetails} utmContent="super_destaque" />
        </li>
      </ul>
    </div>
  );
}

interface SuperHighlightJobCardProps {
  data: SuperHighlightJob;
}
